/**
 * Citation-jump bus.
 *
 * Bridges the AI panel's citation pills (rendered deep inside
 * `BriefingMarkdown`) to the Apryse `WebViewer` instance(s) that live
 * elsewhere in the page tree (matter mode: in `TabView`'s per-tab
 * instances; inbox mode: in `InboxDocumentViewer`'s single instance).
 *
 * Why an event bus instead of prop-drilling: the AI panel is rendered as
 * a sibling — not a descendant — of every WebViewer owner in the tree.
 * Matter mode has `MatterDetails` rendering `<TabView>` and `<AIPane>`
 * side-by-side; inbox mode has `Inbox` rendering `<InboxDocumentViewer>`
 * and `<AIPane>` side-by-side. To make a citation pill (deep inside
 * `BriefingMarkdown` ← `BriefingTab` ← `AIPane`) drive
 * `Core.documentViewer.setCurrentPage(...)` on the sibling WebViewer
 * we'd otherwise have to either (a) lift `webViewerInstances` from
 * `TabView` up to `MatterDetails` (invasive — `TabView` is one of the
 * largest components in the codebase and touching its render tree is
 * Bugbot-spicy), or (b) introduce a context provider just for this
 * one cross-tree wire. Both are heavier than the contract justifies.
 *
 * The bus keeps each side decoupled: pills `dispatch`, viewers
 * `subscribe`. Matter and inbox can wire independently without knowing
 * about each other; future surfaces (e.g. a popped-out viewer window,
 * the reference-doc rail) can subscribe with the same one-liner.
 *
 * Contract:
 *   - The bus carries one event type, "dms:citation-jump", on `window`.
 *   - Payload shape (see `dispatchCitationJump`): `{ documentId, parsed }`
 *     where `parsed` is the citation object emitted by
 *     `BriefingMarkdown` (`{ kind, body }`), and `documentId` is the
 *     id of the document the AI panel is currently bound to (matter
 *     mode: the active doc in the URL; inbox mode: the selected
 *     mail-intake document).
 *   - Subscribers receive the full payload and decide for themselves
 *     whether to act. The matter listener acts only when the event's
 *     `documentId` matches the doc in the active tab; the inbox
 *     listener acts only when `documentId` matches the selected inbox
 *     doc. This keeps the bus surface flat — no per-listener routing
 *     keys to manage.
 *   - SSR-safe: every accessor short-circuits when `window` is
 *     undefined so this module imports cleanly in the Jest jsdom env
 *     and any future SSR build.
 */

const EVENT_NAME = "dms:citation-jump";

const isBrowser = () =>
  typeof window !== "undefined" && typeof window.dispatchEvent === "function";

/**
 * Pull the (1-indexed) page number out of a parsed citation when the
 * citation kind is "source" and the body carries a page anchor.
 *
 * Two body shapes are accepted, in order of precedence:
 *
 *   1. Parenthetical form  — `<filename> (Page <n>)`
 *      Also: `<filename> (Page <n>-<m>)` (page range; we take `<n>`),
 *            `<filename> (page <n>)`     (case-insensitive),
 *            `<filename> (<n>)`          (bare-page shorthand).
 *      This is the contract The Brief LLM emits per
 *      `attorney_prompts.py` and the prototype's frontend regex,
 *      and is the form the saved-Brief PDF and the AnalysisReport
 *      panel render.
 *
 *   2. Comma-separated form — `<filename>, Page <n>`
 *      Legacy / alternative inbox-briefing shape that some surfaces
 *      may emit. Kept supported so we don't break the chat panel's
 *      citation-jump behavior or any non-Brief surfaces that already
 *      ship the comma form. Trailing period optional.
 *
 * Returns `null` for any other kind, malformed bodies, or non-positive
 * page numbers — callers should treat that as "no page-jump available"
 * rather than "page 0".
 *
 * Example:
 *   parseCitationPage({ kind: "source", body: "C&P_Exam.pdf (Page 12)" })
 *   // → { documentName: "C&P_Exam.pdf", page: 12 }
 *
 *   parseCitationPage({ kind: "source", body: "Rating.pdf (Page 4-6)" })
 *   // → { documentName: "Rating.pdf", page: 4 }   // start of range
 *
 *   parseCitationPage({ kind: "source", body: "C&P_Exam.pdf, Page 12" })
 *   // → { documentName: "C&P_Exam.pdf", page: 12 }
 *
 *   parseCitationPage({ kind: "evidence", body: "STR_Audiology.pdf" })
 *   // → null  (evidence has no page anchor)
 *
 *   parseCitationPage({ kind: "source", body: "foo.pdf" })
 *   // → null  (no page suffix at all)
 */
const PAREN_PAGE_RE =
  /^\s*(.+?)\s*\((?:\s*Page\s+)?(\d+)(?:\s*-\s*\d+)?\s*\)\s*\.?\s*$/i;
const COMMA_PAGE_RE = /^\s*(.+?)\s*,\s*Page\s+(\d+)\s*\.?\s*$/i;

export const parseCitationPage = (parsed) => {
  if (!parsed || typeof parsed !== "object") return null;
  if (parsed.kind !== "source" || typeof parsed.body !== "string") return null;
  // Try parenthetical form first because it's the contract the
  // attorney-brief LLM emits and the form the prototype used. Fall
  // back to comma form so legacy producers / inbox-style citations
  // still resolve.
  const m = parsed.body.match(PAREN_PAGE_RE) || parsed.body.match(COMMA_PAGE_RE);
  if (!m) return null;
  const page = Number.parseInt(m[2], 10);
  if (!Number.isFinite(page) || page < 1) return null;
  return { documentName: m[1].trim(), page };
};

/**
 * Dispatch a citation-jump event on the bus.  No-op when the parsed
 * citation can't be page-resolved (e.g. evidence/graph kinds, or a
 * malformed source body) so callers can wire this as the
 * `BriefingMarkdown.onCitationClick` handler unconditionally and let
 * non-page citations fall through silently.
 *
 * Returns `true` when an event was dispatched, `false` otherwise — handy
 * in tests and for future "if no jump, fall back to a toast" flows.
 */
export const dispatchCitationJump = ({ documentId, parsed } = {}) => {
  if (!isBrowser()) return false;
  const pageInfo = parseCitationPage(parsed);
  if (!pageInfo) return false;
  const event = new CustomEvent(EVENT_NAME, {
    detail: {
      documentId: documentId ?? null,
      parsed,
      page: pageInfo.page,
      documentName: pageInfo.documentName,
    },
  });
  window.dispatchEvent(event);
  return true;
};

/**
 * Dispatch a *new-shape* citation-jump event on the same bus. New-shape
 * citations carry the canonical ``Document.id`` integer (BE-injected),
 * an explicit page number, and a verbatim text ``snippet`` for
 * highlight-on-arrival via Apryse's text-search API. They are emitted
 * by ``BriefingMarkdown`` whenever the markdown ``<a>`` href the
 * backend rewrote matches the ``/matter/<matter_id>/D<doc_id>?page=N&q=<snippet>``
 * shape produced by ``doc_pipeline.briefing.report_markdown``.
 *
 * Why a parallel dispatcher instead of widening ``dispatchCitationJump``:
 * the new-shape carries authoritative cross-doc routing information
 * (target ``matterId`` and ``targetDocumentId``) that the legacy
 * ``parseCitationPage``-based flow doesn't have. Subscribers that
 * already handle the legacy event don't need to re-implement target-
 * doc-tab opening; new-shape subscribers (TabView, InboxDocumentViewer)
 * receive the richer payload and can decide whether to (a) treat as
 * "active doc, just jump page" when ``targetDocumentId`` matches the
 * doc currently in focus, or (b) navigate / open a new tab when it
 * doesn't. Both event types share the same EVENT_NAME so the existing
 * ``subscribeCitationJump`` listener fires for both — subscribers
 * disambiguate via ``detail.shape``.
 *
 * Returns ``true`` when an event was dispatched, ``false`` when the
 * caller didn't supply a non-empty ``targetDocumentId`` and ``page``
 * (matching the legacy dispatcher's "skip silently" contract for
 * unrouteable citations).
 */
export const dispatchNewShapeCitationJump = ({
  matterId,
  targetDocumentId,
  page,
  searchQuery,
} = {}) => {
  if (!isBrowser()) return false;
  const docId = targetDocumentId;
  const pageNum = Number.parseInt(page, 10);
  if (!docId) return false;
  if (!Number.isFinite(pageNum) || pageNum < 1) return false;
  const event = new CustomEvent(EVENT_NAME, {
    detail: {
      shape: "new",
      matterId: matterId ?? null,
      // ``documentId`` mirrors the legacy field name so the existing
      // TabView listener that reads ``detail.page`` and the new
      // listener that reads ``detail.targetDocumentId`` can both
      // operate on the same payload — fewer surprise undefineds.
      documentId: docId,
      targetDocumentId: docId,
      page: pageNum,
      searchQuery: typeof searchQuery === "string" ? searchQuery : null,
    },
  });
  window.dispatchEvent(event);
  return true;
};

/**
 * Subscribe to citation-jump events.  The handler receives the event's
 * `detail` payload (the same shape `dispatchCitationJump` emits) and is
 * called inside the event-loop's normal microtask — no batching, no
 * coalescing.  Returns an `unsubscribe()` cleanup; idiomatic React usage:
 *
 *   useEffect(() => subscribeCitationJump(handler), [handler]);
 *
 * SSR/test safe: returns a no-op unsubscribe when `window` is undefined
 * so the same hook expression works in jsdom without `if (typeof window
 * === "undefined")` guards in every caller.
 */
export const subscribeCitationJump = (handler) => {
  if (!isBrowser() || typeof handler !== "function") {
    return () => {};
  }
  const listener = (event) => {
    handler(event?.detail || null);
  };
  window.addEventListener(EVENT_NAME, listener);
  return () => {
    window.removeEventListener(EVENT_NAME, listener);
  };
};

/**
 * Apply a page jump to a WebViewer instance.  Centralised here so all
 * subscribers (TabView matter-mode, InboxDocumentViewer inbox-mode, and
 * any future surface) speak the same Apryse API and inherit the same
 * defensive guards.
 *
 * Mirrors the call shape used by `webViewerOperations.performTextHighlighting`
 * (`setCurrentPage(page) + updateView()`) so pages stay consistent
 * between citation jumps and chat-driven highlight nav.
 *
 * Returns `true` when the jump was applied; `false` when the instance
 * is missing or the page is out of range — callers can use that to
 * surface a toast ("page out of range", "viewer not ready") if they
 * choose to.
 */
export const applyPageJump = (instance, page) => {
  if (!instance || !instance.Core || !instance.Core.documentViewer) return false;
  const documentViewer = instance.Core.documentViewer;
  if (typeof documentViewer.setCurrentPage !== "function") return false;
  const totalPages =
    typeof documentViewer.getPageCount === "function"
      ? documentViewer.getPageCount()
      : null;
  // When the doc hasn't finished loading, getPageCount may return 0 or
  // null. Allow the jump through in that case — Apryse will queue the
  // page change and apply it once the doc is ready. Only block when we
  // have a positive page count AND the requested page exceeds it
  // (otherwise WebViewer would silently clamp without the user
  // realising the jump was off-document).
  if (
    Number.isFinite(totalPages) &&
    totalPages > 0 &&
    (page < 1 || page > totalPages)
  ) {
    return false;
  }
  try {
    documentViewer.setCurrentPage(page);
    if (typeof documentViewer.updateView === "function") {
      documentViewer.updateView();
    }
    return true;
  } catch (err) {
    // Defensive — Apryse occasionally throws on edge-case state (doc
    // mid-load, instance disposed). The viewer stays where it was and
    // the bus keeps working for subsequent clicks.
    if (process.env.NODE_ENV !== "production") {
      console.warn("[citationJump] applyPageJump failed:", err);
    }
    return false;
  }
};

/**
 * Normalise a citation snippet so it has a chance of matching the
 * Apryse PDF text layer.
 *
 * The LLM-extracted snippet that the BE embeds in citation links comes
 * from the markdown extraction pipeline, where the source is parsed,
 * re-encoded, and lightly cleaned up. The PDF text layer the user is
 * actually looking at on screen has gone through a separate
 * tokenisation pass and may carry different code points for the same
 * visual character — smart quotes vs ASCII quotes, em/en dashes vs
 * hyphens, NBSPs / narrow-NBSPs vs ASCII spaces, ligatures (ﬁ / ﬂ /
 * ﬀ) vs separated letters, and so on. Apryse's ``UI.searchText`` is
 * an exact-substring match (not even a fuzzy compare on Unicode
 * normalisation form), so any of those mismatches silently turns a
 * real match into "no results found" with no UI signal beyond an
 * empty highlight band.
 *
 * Normalisation strategy:
 *   - Strip BOMs and zero-width characters that occasionally leak in
 *     from web-scraped sources.
 *   - Replace smart quotes (\u2018-\u201F, \u2032-\u2036) with ASCII
 *     ``'`` / ``"``.
 *   - Replace dashes (em \u2014, en \u2013, hyphens \u2010 / \u2011,
 *     minus \u2212) with ASCII ``-``.
 *   - Replace NBSP (\u00A0), narrow NBSP (\u202F), thin space
 *     (\u2009), word joiner (\u2060), and friends with ASCII space.
 *   - Strip soft hyphens (\u00AD) which the PDF text layer never
 *     contains.
 *   - Replace the horizontal-ellipsis glyph (\u2026) with three ASCII
 *     dots so Apryse can match either form.
 *   - Expand the most common ligatures (ﬁ ﬂ ﬀ ﬃ ﬄ → fi fl ff ffi ffl).
 *   - Collapse runs of whitespace to a single space and trim — the PDF
 *     text layer almost always has the snippet on a single visual line,
 *     and the LLM-emitted snippet often carries pipeline-introduced
 *     newlines or double spaces that wouldn't otherwise match.
 *
 * Returns the empty string for non-string / empty input rather than
 * null so callers can pass the result straight into a length / search
 * pipeline without an extra null guard.
 */
const SMART_QUOTE_DOUBLES = /[\u201C\u201D\u201E\u201F\u2033\u2036]/g;
const SMART_QUOTE_SINGLES = /[\u2018\u2019\u201A\u201B\u2032\u2035]/g;
const DASHES = /[\u2010\u2011\u2013\u2014\u2212]/g;
const SPACE_LIKE = /[\u00A0\u202F\u2007\u2009\u200A\u2060\uFEFF]/g;
const SOFT_HYPHEN = /\u00AD/g;
const LIGATURE_MAP = Object.freeze({
  "\uFB00": "ff",
  "\uFB01": "fi",
  "\uFB02": "fl",
  "\uFB03": "ffi",
  "\uFB04": "ffl",
});
const LIGATURE_RE = /[\uFB00-\uFB04]/g;
const ELLIPSIS = /\u2026/g;
const WHITESPACE_RUN = /\s+/g;

export const normalizeSnippet = (snippet) => {
  if (typeof snippet !== "string") return "";
  if (snippet.length === 0) return "";
  return snippet
    .replace(SMART_QUOTE_DOUBLES, '"')
    .replace(SMART_QUOTE_SINGLES, "'")
    .replace(DASHES, "-")
    .replace(SPACE_LIKE, " ")
    .replace(SOFT_HYPHEN, "")
    .replace(ELLIPSIS, "...")
    .replace(LIGATURE_RE, (ch) => LIGATURE_MAP[ch] || ch)
    .replace(WHITESPACE_RUN, " ")
    .trim();
};

/**
 * Build a progressive-fallback search ladder for a citation snippet.
 *
 * Apryse's ``UI.searchText`` is an exact-substring match against the
 * rendered PDF text layer. The full LLM-extracted snippet is the
 * highest-fidelity query (most distinctive — fewest false matches),
 * but it's also the most likely to fail because:
 *   - line breaks mid-snippet ("the veteran was\ndiagnosed with PTSD"
 *     ≠ "the veteran was diagnosed with PTSD"),
 *   - mid-token truncation ("…diagnosed with PTSD in 200" with the
 *     snippet ending mid-year),
 *   - residual character mismatches that ``normalizeSnippet`` couldn't
 *     fix (rare ligatures, OCR-introduced typos, mid-word PDF
 *     encoding switches).
 *
 * The ladder degrades gracefully:
 *   - Rung 0: the full normalised snippet (best-case exact match).
 *   - Rung 1: the first 7 words (a distinctive phrase — long enough
 *     to be unambiguous on most pages, short enough to survive a
 *     line-break).
 *   - Rung 2: the first 3 words (last-resort hint — may match
 *     multiple times on a page, but the user lands on the right page
 *     and Apryse cycles through the matches with the search controls).
 *
 * Adjacent rungs are deduplicated so a 3-word snippet doesn't fire
 * three identical searches. Empty / whitespace-only input returns
 * an empty array so callers can short-circuit.
 *
 * Word counts are intentional: 7 / 3 are the same numbers used in
 * the prototype's chat-driven highlight pass and broadly mirror
 * web search engines' snippet-matching heuristics.
 */
export const buildSnippetSearchLadder = (snippet) => {
  const normalised = normalizeSnippet(snippet);
  if (normalised.length === 0) return [];
  const words = normalised.split(" ").filter(Boolean);
  if (words.length === 0) return [];
  const ladder = [normalised];
  if (words.length > 7) ladder.push(words.slice(0, 7).join(" "));
  if (words.length > 3) ladder.push(words.slice(0, 3).join(" "));
  // Dedup adjacent rungs (e.g. a 4-word snippet would otherwise
  // collapse to [full, full] after the 7-word push is skipped — we
  // keep [full, first-3-words] which is two distinct attempts).
  const out = [];
  for (const rung of ladder) {
    if (out.length === 0 || out[out.length - 1] !== rung) {
      out.push(rung);
    }
  }
  return out;
};

/**
 * Apply a text-search highlight to a WebViewer instance using Apryse's
 * **headless** ``Core.documentViewer.textSearchInit`` API.
 *
 * Why headless instead of ``UI.searchText``: the user-visible Apryse
 * search panel popping up on every citation click is bad UX — the
 * user shouldn't see how the highlight sausage is made. Worse, that
 * panel is the *only* surface that exposes a "no results" state, so
 * when the LLM-emitted snippet doesn't match the PDF text layer
 * exactly the user sees a spinner that never resolves and an empty
 * panel. Switching to ``textSearchInit`` runs the same search engine
 * against the same text layer, but the result lives in the document
 * surface itself (Apryse renders translucent yellow highlight bands
 * directly on the matched quads via ``Search.Mode.HIGHLIGHT``) and
 * never opens a search panel.
 *
 * The query is run through ``normalizeSnippet`` first so smart
 * quotes, em/en dashes, NBSPs, soft hyphens, and ligatures don't
 * silently wedge an otherwise-valid match. The search itself is
 * case-insensitive and not whole-word because the LLM's verbatim
 * chunk snippet may differ in casing or include trailing punctuation
 * that wouldn't survive whole-word matching. We rely on the
 * snippet's ~80–160 char length to make collisions vanishingly
 * unlikely; if a snippet matches multiple occurrences on a page,
 * Apryse highlights all of them, which is an acceptable degradation.
 *
 * The first ``onResult`` callback also calls
 * ``documentViewer.displaySearchResult`` so the viewport scrolls to
 * the matched paragraph. Stale highlights from a previous citation
 * click are cleared via ``clearSearchResults`` before the new search
 * fires. The Apryse search panel is closed defensively if a previous
 * code path or the user opened it manually.
 *
 * Returns ``true`` when the headless API was invoked; ``false`` when
 * the instance is missing, the headless API isn't available, or the
 * query is empty. Callers can use the falsy return to fall back to a
 * toast or to silently no-op.
 *
 * For the citation-jump pathway prefer ``applySearchHighlightWithLadder``,
 * which adds a progressive-fallback ladder when the normalised full
 * snippet doesn't match. This thin primitive is preserved as the
 * public synchronous shape (no ladder; no rung tracking) for callers
 * that already have their own match-detection plumbing.
 */
const HIGHLIGHT_MODE_FALLBACK = 5; // PAGE_STOP (1) | HIGHLIGHT (4)

const resolveSearchMode = (instance, opts = {}) => {
  const Mode = instance?.Core?.Search?.Mode;
  if (!Mode) return HIGHLIGHT_MODE_FALLBACK;
  let mode = (Mode.PAGE_STOP || 1) | (Mode.HIGHLIGHT || 4);
  if (opts.caseSensitive && Mode.CASE_SENSITIVE) mode |= Mode.CASE_SENSITIVE;
  if (opts.wholeWord && Mode.WHOLE_WORD) mode |= Mode.WHOLE_WORD;
  if (opts.regex && Mode.REGEX) mode |= Mode.REGEX;
  return mode;
};

// Defensively close Apryse's user-visible search panel. Some other
// surface (the toolbar's magnifying-glass button, a previous code
// path, an old build that called ``UI.searchText`` directly) may have
// left it open; we don't want it to peek out from under our headless
// highlight. Failures are swallowed because all of these calls are
// best-effort UI hygiene.
const closeApryseSearchPanel = (UI) => {
  if (!UI) return;
  const panelNames = ["searchPanel", "search", "searchBar"];
  for (const name of panelNames) {
    try {
      if (
        typeof UI.isElementOpen === "function" &&
        UI.isElementOpen(name) &&
        typeof UI.closeElements === "function"
      ) {
        UI.closeElements([name]);
      }
    } catch {
      // Element name not recognised by this Apryse build — try next.
    }
  }
};

export const applySearchHighlight = (instance, query) => {
  if (!instance || !instance.Core || !instance.Core.documentViewer) {
    return false;
  }
  if (typeof query !== "string" || query.trim().length === 0) return false;
  const { documentViewer } = instance.Core;
  if (typeof documentViewer.textSearchInit !== "function") return false;
  // Normalise even on the synchronous primitive so callers that
  // bypass the ladder still get the smart-quote / dash / NBSP fix.
  const normalised = normalizeSnippet(query);
  const finalQuery = normalised.length > 0 ? normalised : query;
  const mode = resolveSearchMode(instance);
  try {
    closeApryseSearchPanel(instance.UI);
    if (typeof documentViewer.clearSearchResults === "function") {
      documentViewer.clearSearchResults();
    }
    const FOUND = instance?.Core?.Search?.ResultCode?.FOUND ?? 2;
    let scrolled = false;
    documentViewer.textSearchInit(finalQuery, mode, {
      fullSearch: false,
      onResult: (result) => {
        // PAGE_STOP fires onResult at page boundaries with
        // ``resultCode === PAGE``. Only treat FOUND as a real match
        // — otherwise we'd scroll the viewport on a page-boundary
        // pseudo-event for searches that found nothing. Older Apryse
        // mocks may omit ``resultCode`` entirely; treat that as FOUND
        // so legacy callers / tests stay green.
        const code = result?.resultCode;
        if (code !== undefined && code !== FOUND) return;
        if (scrolled) return;
        scrolled = true;
        try {
          if (typeof documentViewer.displaySearchResult === "function") {
            documentViewer.displaySearchResult(result);
          }
        } catch {
          // Apryse occasionally throws on edge-case state (mid-load,
          // disposed instance) — the highlight band has still been
          // drawn by the renderer, so the user gets something useful
          // even without the auto-scroll.
        }
      },
    });
    return true;
  } catch (err) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("[citationJump] applySearchHighlight failed:", err);
    }
    return false;
  }
};

/**
 * Apply a text-search highlight with a progressive fallback ladder.
 *
 * This is the function citation-jump subscribers should use. It
 * normalises the snippet, builds a ladder of progressively shorter
 * queries (full → first 7 words → first 3 words), and tries each
 * one in turn — falling back to the next rung whenever Apryse reports
 * no matches.
 *
 * Match detection uses Apryse's **headless** ``textSearchInit`` API
 * on ``Core.documentViewer``. Each rung calls
 * ``documentViewer.textSearchInit(rung, mode, { fullSearch: true,
 * onResult, onDocumentEnd, onError })`` so the search runs against
 * the same text-layer index the user-facing search panel would use,
 * but the result lives in the document surface itself: Apryse paints
 * the yellow highlight band directly via ``Search.Mode.HIGHLIGHT``,
 * and we manually call ``displaySearchResult`` on the first match
 * to scroll the viewport. The user-visible Apryse search panel is
 * never opened (and any panel left open by a previous code path is
 * defensively closed via ``closeApryseSearchPanel``).
 *
 * Counting matches: ``onResult`` may fire many times per rung (once
 * per matched quad). We tally ``matchCount`` and trigger the scroll
 * only on the first FOUND callback (guarded by a ``scrolled`` flag
 * so a paragraph that matches multiple times doesn't make the
 * viewport hop around). The ladder advances on ``onDocumentEnd``,
 * which Apryse fires once after ``fullSearch: true`` completes —
 * regardless of match count — so matched and unmatched rungs both
 * settle deterministically. ``onError`` (regex / build-edge cases)
 * is treated as a miss so the ladder keeps moving.
 *
 * Watchdog: if neither ``onDocumentEnd`` nor ``onError`` fires within
 * ``opts.timeoutMs`` (default 1500ms — generous because Apryse's
 * search is async and a full-doc sweep on a long document can
 * actually take ~1s), we treat the rung as a miss and advance.
 * Without the watchdog a wedged rung would silently swallow the
 * citation-jump.
 *
 * On a matched rung the highlight band is preserved (no
 * ``clearSearchResults`` after the match) — the yellow band is the
 * user-visible signal that the citation landed. On an un-matched
 * rung we wipe partial highlights via ``clearSearchResults`` before
 * retrying so the user never sees stale matches from a prior rung.
 *
 * If every rung fails, we log a structured ``console.warn`` so the
 * miss is visible in browser devtools without blowing up the user's
 * session. The companion ``applyPageJump`` is independent of this —
 * the user always lands on the right page even if no rung matched.
 *
 * Returns ``true`` when the ladder was kicked off (the search is
 * async; the return value does NOT reflect whether any rung matched).
 * Returns ``false`` only when the call couldn't even start — missing
 * instance, missing ``Core.documentViewer``, missing
 * ``textSearchInit``, or a fully empty / non-string snippet.
 *
 * Override defaults via the optional ``opts`` arg:
 *   - ``opts.timeoutMs`` — per-rung watchdog (default 1500ms)
 *   - ``opts.searchOptions`` — bitwise-OR mode flags merged into
 *     ``{ caseSensitive: false, wholeWord: false, regex: false }``
 *   - ``opts.onLadderResult({ rung, index, matched, matchCount, exhausted })``
 *     — observability hook, fired once per rung. ``exhausted`` is
 *     true on the final un-matched rung. Useful for tests + a
 *     future "show toast on miss" flow without re-implementing the
 *     ladder's internal state.
 */
export const applySearchHighlightWithLadder = (instance, snippet, opts = {}) => {
  if (!instance || !instance.Core || !instance.Core.documentViewer) {
    return false;
  }
  const documentViewer = instance.Core.documentViewer;
  if (typeof documentViewer.textSearchInit !== "function") return false;
  const ladder = buildSnippetSearchLadder(snippet);
  if (ladder.length === 0) return false;

  const searchOptions = {
    caseSensitive: false,
    wholeWord: false,
    ...(opts.searchOptions || {}),
  };
  const timeoutMs =
    typeof opts.timeoutMs === "number" && opts.timeoutMs > 0
      ? opts.timeoutMs
      : 1500;
  const onLadderResult =
    typeof opts.onLadderResult === "function" ? opts.onLadderResult : null;
  // Optional page fence. A citation names ONE page; searching the whole
  // document lets a short rung ("He does have") match on page 1 and
  // ``displaySearchResult`` scroll the reader away from the page we
  // just landed on. Apryse accepts ``startPage`` / ``endPage`` in the
  // textSearchInit options (WebViewer 8+); builds that predate it
  // ignore the keys, which degrades to the old whole-document sweep.
  const pageRange =
    opts.pageRange &&
    Number.isFinite(opts.pageRange.startPage) &&
    opts.pageRange.startPage >= 1
      ? {
          startPage: Math.floor(opts.pageRange.startPage),
          endPage: Math.floor(
            Number.isFinite(opts.pageRange.endPage) &&
              opts.pageRange.endPage >= opts.pageRange.startPage
              ? opts.pageRange.endPage
              : opts.pageRange.startPage
          ),
        }
      : null;

  // FOUND is the only ResultCode that signals a real match. PAGE
  // (page-stop) and DONE (legacy) arrive on some builds; the
  // numeric fallback (2) covers test envs and older builds where
  // ``Core.Search`` isn't on the instance.
  const FOUND = instance?.Core?.Search?.ResultCode?.FOUND ?? 2;
  const mode = resolveSearchMode(instance, searchOptions);

  // Defensive UI hygiene — close any panel left open by a previous
  // code path so the headless highlight can't be hidden behind the
  // user-visible Apryse search panel.
  closeApryseSearchPanel(instance.UI);

  const safeClearSearchResults = () => {
    try {
      if (typeof documentViewer.clearSearchResults === "function") {
        documentViewer.clearSearchResults();
      }
    } catch {
      // Apryse occasionally throws while disposing — keep the
      // ladder moving regardless.
    }
  };

  const tryRung = (index) => {
    if (index >= ladder.length) {
      if (process.env.NODE_ENV !== "production") {
        console.warn(
          "[citationJump] applySearchHighlightWithLadder: no rung matched",
          {
            originalSnippet: snippet,
            ladder,
          }
        );
      }
      return;
    }
    const rung = ladder[index];
    let resolved = false;
    let matchCount = 0;
    let scrolled = false;
    let watchdog = null;

    const advance = (matched) => {
      if (resolved) return;
      resolved = true;
      if (watchdog) clearTimeout(watchdog);
      // On a miss, wipe whatever partial highlights this rung may
      // have painted before either the watchdog tripped or
      // ``onDocumentEnd`` confirmed zero matches. On a match, KEEP
      // the yellow band — it's the user-visible signal that the
      // citation landed.
      if (!matched) safeClearSearchResults();
      if (onLadderResult) {
        try {
          onLadderResult({
            rung,
            index,
            matched,
            matchCount,
            exhausted: !matched && index === ladder.length - 1,
          });
        } catch {
          // Caller hook misbehaving shouldn't break the ladder.
        }
      }
      if (!matched) tryRung(index + 1);
    };

    // Pre-search clear so a previous rung's stale highlights or
    // any non-citation search left-over doesn't leak into this
    // attempt.
    safeClearSearchResults();

    watchdog = setTimeout(() => {
      // Neither onDocumentEnd nor onError fired in time. Apryse may
      // be wedged on a long doc / no text layer / mid-dispose.
      // Treat as a miss so the ladder keeps moving.
      advance(false);
    }, timeoutMs);

    try {
      documentViewer.textSearchInit(rung, mode, {
        fullSearch: true,
        ...(pageRange || {}),
        onResult: (result) => {
          if (resolved) return;
          // PAGE / DONE codes can arrive here on some builds —
          // only count FOUND. ``result`` may be missing
          // ``resultCode`` entirely on older Apryse mocks; treat
          // that as FOUND so legacy behaviour stays compatible.
          const code = result?.resultCode;
          if (code !== undefined && code !== FOUND) return;
          matchCount += 1;
          if (!scrolled) {
            scrolled = true;
            try {
              if (typeof documentViewer.displaySearchResult === "function") {
                documentViewer.displaySearchResult(result);
              }
            } catch {
              // HIGHLIGHT mode has already painted the yellow
              // band, so the user still sees the match even
              // without the auto-scroll.
            }
          }
        },
        onDocumentEnd: () => {
          advance(matchCount > 0);
        },
        onError: () => {
          // Regex syntax error or other Apryse-internal failure on
          // this rung — treat as a miss so the ladder still
          // progresses.
          advance(false);
        },
      });
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.warn(
          "[citationJump] applySearchHighlightWithLadder: textSearchInit threw",
          { rung, err }
        );
      }
      advance(false);
    }
  };

  tryRung(0);
  return true;
};

/*
 * ── Arrive-and-confirm landings ──────────────────────────────────────
 *
 * Until 2026-09-15 a citation click fired ``setCurrentPage`` once, on a
 * timer, and hoped. Three other hands were on the same wheel inside the
 * same half-second after ``documentLoaded``: "restore the page you last
 * had open" (100 ms + 50 ms), the tab-switch repaint nudge (a zoom
 * wiggle on the next frame), and — on the URL deep-link path — nothing
 * at all re-applied the page after the restore overwrote it. Whichever
 * fired last won, and nobody checked where the reader actually ended
 * up. On a VAMC record "last page open" is the note index, which is
 * where Matthew kept landing (Glaspie, 2026-09-15; Stanton, 2026-09-10:
 * "These links have to work. No option.").
 *
 * The contract now:
 *   1. A click that must open another document REGISTERS its landing
 *      (``setPendingCitationLanding``) before navigating. The loader
 *      consumes it on ``documentLoaded`` (``takePendingCitationLanding``)
 *      and, when one exists, does NOT restore the last-viewed page.
 *   2. ``landOnCitation`` asserts the page, then keeps checking
 *      ``getCurrentPage()`` for a short pin window and re-asserts if
 *      anything moved it. Only once the viewer reports the cited page
 *      does the quote highlight start — fenced to that page so a short
 *      rung can't scroll the reader elsewhere.
 *   3. Nothing here relies on a single well-timed ``setTimeout``.
 */

const pendingLandings = new Map();
const PENDING_LANDING_TTL_MS = 60_000;

const landingKey = (documentId) => {
  if (documentId == null) return null;
  const raw = String(documentId).trim();
  if (raw.length === 0) return null;
  // Tab ids are ``D<id>``; document records carry the bare id. Accept
  // both so callers don't have to remember which side they're on.
  return raw.startsWith("D") && raw.length > 1 ? raw.slice(1) : raw;
};

/**
 * Remember that the next load of ``documentId`` is citation-driven and
 * must land on ``page`` (highlighting ``searchQuery`` when given).
 * Returns ``true`` when a landing was recorded.
 */
export const setPendingCitationLanding = (documentId, { page, searchQuery } = {}) => {
  const key = landingKey(documentId);
  const pageNum = Number.parseInt(page, 10);
  if (!key || !Number.isFinite(pageNum) || pageNum < 1) return false;
  pendingLandings.set(key, {
    page: pageNum,
    searchQuery:
      typeof searchQuery === "string" && searchQuery.trim().length > 0
        ? searchQuery
        : null,
    createdAt: Date.now(),
  });
  return true;
};

/**
 * Consume (and forget) the pending landing for ``documentId``. Landings
 * older than a minute are dropped: the navigation they belonged to has
 * long since resolved one way or the other, and a stale landing must
 * never hijack an unrelated open of the same document.
 */
export const takePendingCitationLanding = (documentId) => {
  const key = landingKey(documentId);
  if (!key) return null;
  const entry = pendingLandings.get(key);
  if (!entry) return null;
  pendingLandings.delete(key);
  if (Date.now() - entry.createdAt > PENDING_LANDING_TTL_MS) return null;
  return { page: entry.page, searchQuery: entry.searchQuery };
};

/** Test/diagnostic helper — wipe every pending landing. */
export const clearPendingCitationLandings = () => {
  pendingLandings.clear();
};

/**
 * Work out the landing a freshly loaded document owes the reader, from
 * the three places a citation can leave its page:
 *
 *   1. the pending-landing register (cross-document clicks in TabView),
 *   2. router state ``highlightMetadata`` (chat citations and older
 *      callers) — honoured only when it names this document or names
 *      none at all,
 *   3. the URL ``?page=N&q=…`` deep link (cross-matter clicks, links
 *      pasted from a saved Brief).
 *
 * Returns ``{ page, searchQuery }`` or ``null`` when the open is an
 * ordinary one and the last-viewed page should be restored as usual.
 */
export const resolveCitationLanding = (
  documentId,
  { highlightMetadata, search } = {}
) => {
  const pending = takePendingCitationLanding(documentId);
  if (pending) return pending;

  const key = landingKey(documentId);
  if (Array.isArray(highlightMetadata) && highlightMetadata.length > 0) {
    const entry = highlightMetadata.find(
      (m) => m && Number.isFinite(Number.parseInt(m.page, 10)) && Number.parseInt(m.page, 10) >= 1
    );
    if (entry) {
      const forDoc = landingKey(entry.documentId);
      if (!forDoc || !key || forDoc === key) {
        return {
          page: Number.parseInt(entry.page, 10),
          searchQuery:
            typeof entry.chunk_content === "string" && entry.chunk_content.trim().length > 0
              ? entry.chunk_content
              : null,
        };
      }
    }
  }

  const query =
    typeof search === "string"
      ? search
      : isBrowser() && typeof window.location?.search === "string"
        ? window.location.search
        : "";
  if (query) {
    try {
      const params = new URLSearchParams(query);
      const pageNum = Number.parseInt(params.get("page") ?? "", 10);
      if (Number.isFinite(pageNum) && pageNum >= 1) {
        const q = (params.get("q") || "").trim();
        return { page: pageNum, searchQuery: q.length > 0 ? q : null };
      }
    } catch {
      // Malformed query string — treat as no deep link.
    }
  }
  return null;
};

const currentPageOf = (documentViewer) => {
  try {
    if (typeof documentViewer.getCurrentPage === "function") {
      const n = documentViewer.getCurrentPage();
      return Number.isFinite(n) ? n : null;
    }
  } catch {
    // Mid-load / disposed — treat as unknown.
  }
  return null;
};

/**
 * Land a WebViewer on a cited page and prove it got there.
 *
 * - Applies the jump immediately.
 * - Polls ``getCurrentPage()`` every ``intervalMs`` (default 100) for
 *   ``pinMs`` (default 2000). If anything else moved the viewer during
 *   that window, the jump is re-applied — up to ``maxReasserts`` times
 *   so a reader who genuinely scrolls away is not fought forever.
 * - The first time the viewer confirms the page, the quote highlight
 *   starts (fenced to the page). If the document has no text layer the
 *   highlight finds nothing and the page is the deliverable.
 * - ``onSettled({ page, landed, reasserts })`` fires once, when the pin
 *   window closes or the routine is cancelled.
 *
 * Returns a cancel function; call it if the tab is torn down.
 */
export const landOnCitation = (instance, { page, searchQuery } = {}, opts = {}) => {
  const noop = () => {};
  if (!instance || !instance.Core || !instance.Core.documentViewer) return noop;
  const pageNum = Number.parseInt(page, 10);
  if (!Number.isFinite(pageNum) || pageNum < 1) return noop;
  const documentViewer = instance.Core.documentViewer;

  const intervalMs =
    typeof opts.intervalMs === "number" && opts.intervalMs > 0 ? opts.intervalMs : 100;
  const pinMs = typeof opts.pinMs === "number" && opts.pinMs >= 0 ? opts.pinMs : 2000;
  const maxReasserts =
    typeof opts.maxReasserts === "number" && opts.maxReasserts >= 0 ? opts.maxReasserts : 8;
  const onSettled = typeof opts.onSettled === "function" ? opts.onSettled : null;
  const highlight =
    typeof opts.highlight === "function"
      ? opts.highlight
      : (inst, snippet, range) =>
          applySearchHighlightWithLadder(inst, snippet, { pageRange: range });

  let cancelled = false;
  let timer = null;
  let reasserts = 0;
  let highlighted = false;
  let landed = false;
  const startedAt = Date.now();

  const finish = () => {
    if (timer) clearTimeout(timer);
    timer = null;
    if (onSettled) {
      try {
        onSettled({ page: pageNum, landed, reasserts });
      } catch {
        // Observability hook must not break the landing.
      }
    }
  };

  const startHighlight = () => {
    if (highlighted) return;
    highlighted = true;
    if (!searchQuery) return;
    try {
      highlight(instance, searchQuery, { startPage: pageNum, endPage: pageNum });
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("[citationJump] landOnCitation: highlight failed", err);
      }
    }
  };

  const tick = () => {
    if (cancelled) return;
    const current = currentPageOf(documentViewer);
    if (current === pageNum) {
      landed = true;
      startHighlight();
    } else if (current !== null && reasserts < maxReasserts) {
      reasserts += 1;
      applyPageJump(instance, pageNum);
    }
    if (Date.now() - startedAt >= pinMs) {
      if (!landed && process.env.NODE_ENV !== "production") {
        console.warn("[citationJump] landOnCitation: viewer never reported the cited page", {
          page: pageNum,
          current,
          reasserts,
        });
      }
      finish();
      return;
    }
    timer = setTimeout(tick, intervalMs);
  };

  applyPageJump(instance, pageNum);
  tick();

  return () => {
    if (cancelled) return;
    cancelled = true;
    finish();
  };
};

/**
 * Route a parsed citation (from ``BriefingMarkdown``) to the appropriate
 * dispatcher. Centralised here so every panel surface that renders a
 * Brief / Briefing / Context-Analysis markdown body shares one source of
 * truth for routing. Previously the same logic was triplicated across
 * ``AnalysisReport.js``, ``BriefingTab.js``, and ``ContextAnalysisTab.js``;
 * a fix in one would silently miss the others (Bugbot Low #PR-106).
 *
 * Routing rules:
 *   1. ``shape === "new"`` (BE-rewritten ``/matter/<id>/D<doc_id>?...``):
 *      dispatch the new-shape event with the full ``matterId / docId /
 *      page / snippet`` payload so subscribers can open the right doc
 *      tab regardless of which doc the AI panel is currently bound to.
 *   2. ``shape === "legacy-anchor"`` (BE-rewritten ``#cite-source``
 *      anchor for a citation the link builder couldn't resolve):
 *      no-op. The pill is rendered non-clickable upstream, so this
 *      branch is belt-and-braces for future callers.
 *   3. Anything else (sentinel-shaped pills emitted from un-rewritten
 *      markdown, e.g. ``BriefingTab`` / ``ContextAnalysisTab`` reading
 *      raw inbox-briefing or context-analysis text): fall through to
 *      the legacy ``parseCitationPage``-driven dispatcher, which
 *      assumes the AI panel is bound to ``activeDocumentId`` and just
 *      jumps the active tab to the parsed page.
 *
 * ``activeDocumentId`` is only consulted in the legacy fallback; the
 * new-shape path carries its own target doc id and ignores it. Callers
 * can pass ``null`` / ``undefined`` for surfaces that don't have an
 * active doc context (e.g. a future preview pane).
 */
export const handleCitationClick = (parsed, activeDocumentId) => {
  if (!parsed) return;
  if (parsed.shape === "new") {
    dispatchNewShapeCitationJump({
      matterId: parsed.matterId ?? null,
      targetDocumentId: parsed.docId,
      page: parsed.page,
      searchQuery: parsed.snippet,
    });
    return;
  }
  if (parsed.shape === "legacy-anchor") {
    return;
  }
  dispatchCitationJump({
    documentId: activeDocumentId,
    parsed,
  });
};

/**
 * Build the React Router target path for a cross-document citation jump.
 *
 * Used by the new-shape citation subscriber when the user clicks an
 * evidence pill that points at a *different* document than the one
 * currently in the active tab. The destination must:
 *
 *   1. Land on the right document URL (`/matter/<matterId>/D<docId>`),
 *   2. Carry the citation page so the route loader can deep-link
 *      directly into the right page (`?page=<n>`),
 *   3. Optionally carry the citation snippet so the in-app highlight
 *      step can re-fire on the new tab (`?q=<snippet>`),
 *   4. **Preserve the AI panel state** so the Brief that the user just
 *      clicked from doesn't disappear on doc switch (`?ai=analysis`).
 *
 * The AI-tab preservation (#4) is the load-bearing part of this fix:
 * without it, the new-doc URL has no `?ai=` param, the AI panel reverts
 * to its default tab (briefing) for that doc, and the user feels like
 * the Brief has vanished. By forcing `?ai=analysis` (the panel where
 * citation pills live), the panel mount stays on the right tab across
 * the navigation.
 *
 * Returns a fully-formed path string suitable for ``navigate(...)`` or
 * ``<Link to={...}>``. Returns ``null`` when ``matterId`` /
 * ``targetDocumentId`` are missing or the page isn't a positive
 * integer (callers should fall back to a no-op + log).
 *
 * Pure function — no side effects, no DOM, no React Router. Easy to
 * unit-test; safe to call from any code path.
 */
export const buildCrossDocumentCitationPath = ({
  matterId,
  targetDocumentId,
  page,
  searchQuery,
  // The AI tab to preserve in the destination URL. Defaults to
  // "analysis" (where citation pills live in matter mode); callers can
  // override for e.g. inbox-mode flows that want to land on
  // "context_analysis" instead. Values that aren't recognised by the
  // AI-pane URL helper get rejected upstream by ``buildAIPaneSearch``;
  // we keep the param explicit here to make the contract obvious at
  // the call site.
  aiTab = "analysis",
} = {}) => {
  if (matterId == null || targetDocumentId == null) return null;
  if (typeof page !== "number" || !Number.isFinite(page) || page < 1) {
    return null;
  }
  const params = new URLSearchParams();
  params.set("page", String(Math.floor(page)));
  if (typeof searchQuery === "string" && searchQuery.length > 0) {
    params.set("q", searchQuery);
  }
  // Force the AI panel to stay on the citation-bearing tab on the new
  // doc. See block comment above — this is the fix for "the Brief
  // disappears when I click an evidence pill in another document".
  params.set("ai", aiTab);
  return `/matter/${matterId}/D${targetDocumentId}?${params.toString()}`;
};

// Exported for tests that want to assert raw event-name parity without
// hard-coding the string everywhere.
export const __CITATION_JUMP_EVENT_NAME__ = EVENT_NAME;
